import { useState, useEffect, useRef } from 'react';
import { createTool } from '../../utils/api';
import { useToast } from '../../context/ToastContext';

const WELCOME = {
  role: 'assistant',
  content: "List all the tools you want to add — just dump them however you like. I'll ask a couple of quick questions then generate records for all of them at once.",
};

const CAT_LABEL = (c) => c ? c.charAt(0).toUpperCase() + c.slice(1) : '';

const ToolBulkIntakeModal = ({ onClose, onAllSaved }) => {
  const { showToast } = useToast();
  const [messages, setMessages] = useState([WELCOME]);
  const [input, setInput] = useState('');
  const [thinking, setThinking] = useState(false);
  const [pendingTools, setPendingTools] = useState(null); // array when AI produces output
  const [selected, setSelected] = useState([]);           // indices of checked items
  const [saving, setSaving] = useState(false);
  const bottomRef = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape' && !thinking && !saving) onClose(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose, thinking, saving]);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, pendingTools]);

  const send = async () => {
    const text = input.trim();
    if (!text || thinking) return;
    const userMsg = { role: 'user', content: text };
    const history = messages.filter((m) => m.role !== 'system');
    const nextMessages = [...history, userMsg];
    setMessages([...messages, userMsg, { role: 'assistant', content: '' }]);
    setInput('');
    setThinking(true);
    setPendingTools(null);

    try {
      const response = await fetch('/api/intake/tool-bulk-chat', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ messages: nextMessages.map((m) => ({ role: m.role, content: m.content })) }),
      });

      if (!response.ok) {
        const errData = await response.json().catch(() => ({}));
        throw new Error(errData.message || `HTTP ${response.status}`);
      }

      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let streamedText = '';
      let buffer = '';

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n');
        buffer = lines.pop();
        for (const line of lines) {
          if (!line.startsWith('data: ')) continue;
          let evt;
          try { evt = JSON.parse(line.slice(6)); } catch { continue; }
          if (evt.type === 'text') {
            streamedText += evt.text;
            setMessages((prev) => {
              const updated = [...prev];
              updated[updated.length - 1] = { role: 'assistant', content: streamedText };
              return updated;
            });
          } else if (evt.type === 'done') {
            setThinking(false);
            if (evt.done && Array.isArray(evt.project) && evt.project.length > 0) {
              setPendingTools(evt.project);
              setSelected(evt.project.map((_, i) => i)); // all selected by default
              if (evt.reply != null) {
                setMessages((prev) => {
                  const updated = [...prev];
                  updated[updated.length - 1] = { role: 'assistant', content: evt.reply };
                  return updated;
                });
              }
            }
          } else if (evt.type === 'error') {
            setThinking(false);
            showToast(evt.message || 'AI error', 'error');
          }
        }
      }
    } catch (err) {
      setThinking(false);
      showToast(err.message || 'Connection error', 'error');
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); send(); }
  };

  const toggleSelect = (i) => {
    setSelected((prev) =>
      prev.includes(i) ? prev.filter((x) => x !== i) : [...prev, i]
    );
  };

  const addSelected = async () => {
    if (!pendingTools || selected.length === 0 || saving) return;
    setSaving(true);
    const toAdd = selected.map((i) => pendingTools[i]);
    try {
      const results = await Promise.all(toAdd.map((t) => createTool(t)));
      onAllSaved(results.map((r) => r.data));
      onClose();
    } catch (err) {
      showToast(err.response?.data?.message || 'Failed to save some tools', 'error');
      setSaving(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={() => !thinking && !saving && onClose()}>
      <div className="modal tool-bulk-modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>✨ Add Multiple Tools</h2>
          <button className="modal-close" onClick={onClose} disabled={thinking || saving}>✕</button>
        </div>

        <div className="tool-intake-messages">
          {messages.map((msg, i) => (
            <div key={i} className={`fr-message fr-message-${msg.role}`}>
              {msg.content || (thinking && i === messages.length - 1
                ? <span className="fr-thinking">thinking…</span>
                : null)}
            </div>
          ))}

          {pendingTools && (
            <div className="bulk-preview">
              <p className="bulk-preview-label">
                {pendingTools.length} tool{pendingTools.length !== 1 ? 's' : ''} ready — uncheck any that look wrong:
              </p>
              <div className="bulk-checklist">
                {pendingTools.map((tool, i) => (
                  <label key={i} className={`bulk-item${selected.includes(i) ? '' : ' bulk-item-unchecked'}`}>
                    <input
                      type="checkbox"
                      checked={selected.includes(i)}
                      onChange={() => toggleSelect(i)}
                    />
                    <span className="bulk-item-name">{tool.name}</span>
                    <span className="bulk-item-meta">
                      {[CAT_LABEL(tool.category), tool.condition, tool.location].filter(Boolean).join(' · ')}
                    </span>
                  </label>
                ))}
              </div>
              <div style={{ display: 'flex', gap: '0.5rem', marginTop: '0.75rem', flexWrap: 'wrap' }}>
                <button
                  className="btn btn-primary"
                  onClick={addSelected}
                  disabled={saving || selected.length === 0}
                >
                  {saving ? 'Adding…' : `Add ${selected.length} Tool${selected.length !== 1 ? 's' : ''}`}
                </button>
                <button
                  className="btn btn-secondary"
                  onClick={() => { setPendingTools(null); inputRef.current?.focus(); }}
                  disabled={saving}
                >
                  Keep chatting
                </button>
                <button className="btn btn-secondary" onClick={() => setSelected(pendingTools.map((_, i) => i))} disabled={saving}>
                  Select all
                </button>
              </div>
            </div>
          )}

          <div ref={bottomRef} />
        </div>

        <div className="fr-input-row">
          <textarea
            ref={inputRef}
            className="fr-input"
            rows={3}
            placeholder="e.g. hammer, tape measure, 24&quot; level, Milwaukee M18 drill, jigsaw, circular saw, set of screwdrivers…"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={handleKeyDown}
            disabled={thinking || saving}
            autoFocus
          />
          <button
            className="btn btn-primary fr-send-btn"
            onClick={send}
            disabled={thinking || saving || !input.trim()}
          >
            {thinking ? '…' : 'Send'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ToolBulkIntakeModal;
